// @flow

import * as React from 'react';
import shuffle from 'shuffle-array';
import styled from 'styled-components';
import Button from 'app/components/shared/Button';
import classNames from 'classnames';
import Icon from 'app/components/shared/Icon';

import DropdownButton from './dropdown-button';
import Dropdown from 'app/components/shared/Dropdown';

import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch, SearchBox, connectHits, connectStateResults,
  type SearchState, type SearchResults, type TDoc, type THit
} from 'react-instantsearch-dom';
import 'instantsearch.css/themes/reset.css';
import './help-dropdown.css';

import { trackEvent } from 'app/lib/matomo';

const EVENT_CATEGORY = 'Help Dropdown';

const IMAGE_DIAMETER = 64;
const IMAGE_OVERLAP_HORIZONTAL = 48;

const MUGSHOT_AREA_PADDING_HORIZONTAL = 15 + IMAGE_OVERLAP_HORIZONTAL;
// NOTE: 480 is the width of the content area of the Dialog at full size.
const MUGSHOT_AREA_MAX_WIDTH = 480 - MUGSHOT_AREA_PADDING_HORIZONTAL * 2;

const SUPPORT_PEOPLE = [
  { image: "jason", name: "Jason Jacob", backgroundColor: "rgb(86, 106, 56)" }
];

const PEOPLE = [
  { image: "keithpitt", name: "Keith Pitt", backgroundColor: "rgb(216,138,139)" },
  { image: "harriet", name: "Harriet Lawrence", backgroundColor: "rgb(206,153,117)" },
  { image: "ticky", name: "Jessica Stokes", backgroundColor: "rgb(236,188,217)" },
  { image: "sj26", name: "Sam Cochran", backgroundColor: "rgb(179,166,187)" },
  { image: "toolmantim", name: "Tim Lucas", backgroundColor: "rgb(129,188,228)" },
  { image: "lox", name: "Lachlan Donald", backgroundColor: "rgb(149,149,143)" },
  { image: "eleanor", name: "Eleanor Kiefel Haggerty", backgroundColor: "rgb(153,153,255)" },
  { image: "matthew", name: "Matthew Draper", backgroundColor: "rgb(173,196,235)" },
  { image: "chloe", name: "Chloe Hutchinson", backgroundColor: "rgb(178, 187, 204)" },
  { image: "james", name: "James Healy", backgroundColor: "rgb(115, 210, 238)" },
  { image: "paul", name: "Paul Annesley", backgroundColor: "rgb(244, 209, 223)" },
  { image: "ben", name: "Ben Christensen", backgroundColor: "rgb(184, 163, 234)" },
  { image: "cath", name: "Cath Jones", backgroundColor: "rgb(164, 185, 129)" },
  { image: "eleanort", name: "Eleanor Toulmin", backgroundColor: "rgb(207, 223, 192)" },
  { image: "fred", name: "Fred Showell", backgroundColor: "rgb(218,202,72)" }
];


const calculateRowWidth = (count) => (count * IMAGE_DIAMETER - (count - 1) * (IMAGE_OVERLAP_HORIZONTAL * 2));

const SmallHeader = styled.h4`
  font-size: 10px;
  color: #6c6c6c;
  text-transform: uppercase;
  margin: 10px 0;
`;

const DividingLine = styled.div`
  height: 1px
  width: calc(100%+30px);
  background-color: #ddd;
  margin: 15px -15px 15px -15px;
`;

const Mugshots = styled.div`
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  max-width: ${({ children }) => {
    const count = React.Children.count(children);

    const minimumRows = Math.ceil(calculateRowWidth(count) / MUGSHOT_AREA_MAX_WIDTH);

    return calculateRowWidth(Math.ceil(count / minimumRows)) + MUGSHOT_AREA_PADDING_HORIZONTAL * 2;
  }}px;

  &:after {
    content: '';
    position: absolute;
    width: 75%;
    height: 100%;
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 42%, rgba(255,255,255,0.8) 100%);
    right: 0;
    top: 0;
    pointer-events: none;
    z-index: 11;
  }
`;

const Mugshot = styled.img`
  background-color: ${(props) => props.backgroundColor};
  width: ${IMAGE_DIAMETER}px;
  height: ${IMAGE_DIAMETER}px;
  border-width: 4px;
  margin-left: ${-IMAGE_OVERLAP_HORIZONTAL}px;

  &:first-child {
    margin-left: 0;
  }
`;

const renderMugshotPreload = ({ image }: Person) => (
  <link
    key={image}
    rel="preload"
    href={require(`../../../images/people/${image}-small.jpg`)}
    as="image"
  />
);

type Person = {
  name: string,
  image: string,
  backgroundColor: string
};

type SuggestedDoc = {
  title: string,
  url: string
};

type Props = {
  suggestedDocumentation: Array<SuggestedDoc>
};

type State = {
  people: Array<Person> | null,
  isDropdownVisible: boolean
};

const searchClient = algoliasearch(
  '1IDL1HEBJ6',
  '3d6f41b2f8d0107f8a122b78f10a3aef'
);

type SearchProps = {
  searchState: SearchState,
  searchResults?: SearchResults<TDoc>,
  children: any,
  suggestedDocumentation: Array<SuggestedDoc>
};

const StateResults = (props: SearchProps) => {
  const hasQuery = props.searchState && props.searchState.query;
  const hasResults = props.searchResults && props.searchResults.nbHits !== 0;
  if (hasQuery && hasResults) {
    return (<div>{props.children}</div>);
  } else if (hasQuery && !hasResults) {
    return (<div className="center my3 black">No results found for {props.searchState.query}</div>);
  }
  if (props.suggestedDocumentation.length < 1) {
    return null;
  }
  return (
    <div className="py1">
      <SmallHeader>Suggested Documentation</SmallHeader>
      {props.suggestedDocumentation.map(({ title, url }) => {
        return (<a key={url} className="black bold line-height-4 text-decoration-none flex flex-none hover-lime mt1 docs-suggestion-link docs-link" style={{ fontSize: "14px" }} href={url}>
          <Icon icon="document" className="flex-none" />
          <span className="flex-none ml1" style={{ lineHeight: "24px" }}>{title}</span>
        </a>);
      }
      )}
    </div>
  );
};

const CustomResults = connectStateResults(StateResults);

type HitProps = {
  hits: Array<THit>
};

const Hits = (props: HitProps) => (
  <div style={{ margin: "10px 0" }}>
    {props.hits.map((hit, index) => {
      // const category = hit.hierarchy.lvl0;
      const pageTitle = hit.hierarchy.lvl1;
      const pageSection = hit.hierarchy.lvl2;
      if (index < 10) {
        return (
          <a key={hit.objectID} className="block black hover-dark-gray text-decoration-none docs-search-link docs-link" style={{ fontSize: "14px", fontWeight: "600", padding: "5px 0" }} href={hit.url}>
            <span dangerouslySetInnerHTML={{ __html: pageTitle }} />
            {pageSection ? <span style={{ fontSize: "10px", opacity: "0.6", textTransform: "uppercase" }} dangerouslySetInnerHTML={{ __html: pageSection }} /> : null}
          </a>
        );
      }
    }
    )}
  </div>
);

const CustomHits = connectHits(Hits);

export default class HelpDropdown extends React.PureComponent<Props, State> {
  static displayName = "Navigation.HelpDropdown";

  state: State = {
    people: SUPPORT_PEOPLE.concat(shuffle(PEOPLE)),
    isDropdownVisible: false
  };

  handleDropdownToggle = (visible: boolean) => {
    this.setState({ isDropdownVisible: visible });

    if (visible) {
      window.addEventListener("keydown", this.checkKey);

      // Setting focus manually, rather than using autofocus prop on the element, because autofocus breaks the presenting transition
      const searchBox = document.getElementsByClassName('ais-SearchBox-input')[0];
      searchBox.focus();
    }

    if (!visible) {
      window.removeEventListener("keydown", this.checkKey);

      // using setTimeout because it doesn't look like we have a callback for the completion of the dismissal animation
      setTimeout(() => this.setState({ people: SUPPORT_PEOPLE.concat(shuffle(PEOPLE)) }), 220);
    }

    trackEvent(EVENT_CATEGORY, visible ? 'Open' : 'Close');
  }

  render() {
    return (
      <React.Fragment>
        {SUPPORT_PEOPLE.map(renderMugshotPreload)}
        {PEOPLE.map(renderMugshotPreload)}
        <Dropdown width={320} className="flex flex-none" onToggle={this.handleDropdownToggle}>
          <DropdownButton className={classNames("flex-none py0 xs-hide sm-hide", { "lime": this.state.isDropdownVisible })}>
            {'Help '}
            <Icon
              icon="down-triangle"
              className="flex-none"
              style={{
                width: 7,
                height: 7,
                marginLeft: '.5em'
              }}
            />
          </DropdownButton>
          {this.renderContent()}
        </Dropdown>
      </React.Fragment>
    );
  }

  renderContent() {
    return (
      <div className="px3 py2">
        <InstantSearch
          indexName="prod_docs"
          searchClient={searchClient}
        >
          <SearchBox submit={null} reset={null} translations={{ placeholder: 'Search Documentation' }} />
          <CustomResults suggestedDocumentation={this.props.suggestedDocumentation}>
            <CustomHits />
          </CustomResults>
        </InstantSearch>

        <Button className="block h4 bold center mt2" href="https://buildkite.com/docs">Browse the Documentation</Button>

        <DividingLine />

        <div className="mx-auto mb3 mt3 semi-bold line-height-4">
          Have a problem, or can’t find what you need?<br />
          Send us an email and we’ll help you out.
        </div>

        <Mugshots>
          {this.state.people && this.state.people.slice(0, 15).map(({ name, image, backgroundColor }, index) => {
            const zValue = 10 - index;
            return (
              <Mugshot
                key={name}
                className="circle border border-white"
                src={require(`../../../images/people/${image}-small.jpg`)}
                alt={name}
                backgroundColor={backgroundColor}
                style={{ zIndex: zValue }}
              />
            );
          }
          )}
        </Mugshots>

        <div className="mt4">
          <Button className="block h4 bold center" href="mailto:support@buildkite.com">Email support@buildkite.com</Button>
        </div>

      </div>
    );
  }

  checkKey(event: KeyboardEvent) {
    event = event || window.event;
    const activeElement = document.activeElement;

    if (activeElement && activeElement.tagName === "INPUT" && (event.key === "ArrowDown" || event.key === "Enter")) {
      event.preventDefault();
      const firstLink = document.getElementsByClassName("docs-link")[0];
      if (firstLink) {
        firstLink.focus();
      }
    }

    if (activeElement && activeElement.classList.contains("docs-link") && event.key === "ArrowDown") {
      event.preventDefault();
      const nextLink = activeElement.nextElementSibling;
      if (nextLink && nextLink.classList.contains("docs-link")) {
        // get around Flow's typing, which asserts that nextElementSibling returns an object of type Element, not HTMLElement
        const htmlLink = ((nextLink: any): HTMLElement);
        htmlLink.focus();
      }
    }

    if (activeElement && activeElement.classList.contains("docs-link") && event.key === "ArrowUp") {
      event.preventDefault();
      const prevLink = activeElement.previousElementSibling;
      if (prevLink && prevLink.classList.contains("docs-link")) {
        // get around Flow's typing, which asserts that previousElementSibling returns an object of type Element, not HTMLElement
        const htmlLink = ((prevLink: any): HTMLElement);
        htmlLink.focus();
      } else {
        // If you press up and there is no link above, give focus to the searchbox
        const searchBox = document.getElementsByClassName('ais-SearchBox-input')[0];
        searchBox.focus();
      }
    }
  }
}
